import { FunctionComponent, useEffect, useState } from "react";
import { useHistory, useParams, useRouteMatch } from "react-router-dom";
import {
  CostCenter,
  CostCenterCloseStatus,
  costCenterDefaultValue,
  MasterCostCenterStatus,
} from "../models/cost_center_model";
import { urls } from "../../../utils/urls";
import { useAuth } from "../../../context_providers/auth/authProvider";
import {
  ToastType,
  useToast,
} from "../../../context_providers/toast/toast_ctx";
import { ACTION, MODULE } from "../../../utils/enums";
import LoadingWidget from "../../../context_providers/modal/loader";
import moment from "moment";
import { fetchGet, fetchPost, fetchPut } from "../../../service/api_client";
import "../../../styles/responsive_shrink.css";
import ModalDialog from "../../../ui/new_components/common/modal";
import { BusinessUnit } from "../../../erp/org/models/business_unit_model";
import { Department } from "../../../erp/org/models/department_model";

interface CostCenterCreateScreenProps {
  data?: CostCenter;
  onClose: (id: CostCenter) => void;
}

const CostCenterCreateScreen: FunctionComponent<CostCenterCreateScreenProps> = (
  props,
) => {
  const { id } = useParams<{ id: string }>();
  const navaigation = useHistory();
  const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/cost_center`;
  const is_update_page = useRouteMatch(
    urls.procurement_masters_cost_center + "/update/:id",
  );

  const [loading, setLoading] = useState(false);
  const [checker, setChecker] = useState(false);
  const [maker, setMaker] = useState(false);

  const { user, isAuthorised } = useAuth();
  // const [canEdit, setCanEdit] = useState(true);
  const [approval, setApproval] = useState(false);
  const [reject, setReject] = useState(false);
  const [edit, setEdit] = useState(false);

  const { showToast } = useToast();
  const [data, setData] = useState<CostCenter>({
    ...(props.data || { ...costCenterDefaultValue }),
  });

  // --- Phase 2: BU + Department dropdown state (new-flow CCs)
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  useEffect(() => {
    if (id && is_update_page) {
      getData(id);
      setEdit(true);
    }
  }, [id]);

  // Fetch active BUs and Departments once on mount; populates the new-flow dropdowns.
  useEffect(() => {
    const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL;
    (async () => {
      try {
        const buRes = await fetchGet(
          `${baseUrl}/api/v3/org/business-unit?all=true&status=Active`
        );
        if (buRes.success) setBusinessUnits(buRes.data.data || []);
        const deptRes = await fetchGet(
          `${baseUrl}/api/v3/org/department?all=true&status=Active`
        );
        if (deptRes.success) setDepartments(deptRes.data.data || []);
      } catch (e: any) {
        // silent — old-flow CCs still work even if these fail to load
      }
    })();
  }, []);

  const getData = async (id: string) => {
    setLoading(true);
    const res = await fetchGet(url + "/" + id);
    if (res.success) {
      setData(res.data);
    } else showToast({ type: ToastType.error, text: res.error });
    setLoading(false);
  };

  const calculatePeriodInMonths = (fromDate: Date, toDate: Date) => {
    if (!fromDate || !toDate) return 0;
    return moment(toDate).diff(moment(fromDate), "months");
  };

  // Whenever fromMonth or toMonth changes, update the period
  useEffect(() => {
    if (data.fromMonth && data.toMonth) {
      setData((prev) => ({
        ...prev,
        period: calculatePeriodInMonths(data.fromMonth!, data.toMonth!),
      }));
    }
  }, [data.fromMonth, data.toMonth]); // Runs whenever dates change

  const CostCenterApprovalStatus = async (status: string) => {
    if (status === MasterCostCenterStatus.rejected && !data.remarks) {
      showToast({
        type: ToastType.error,
        text: "Need Cost Centr Reject Remarks.",
      });
      return;
    } else {
      // console.log("Sending status:", status);
      try {
        const res = await fetchPut(`${url}/${data.id}/approval`, data);

        console.log("API response:", res); // Log the full response for debugging
        showToast({
          type: ToastType.success,
          text: `Cost Center Status: ${status}`,
        });
        navaigation.push(urls.procurement_masters_cost_center);
        // Your existing success/error logic...
      } catch (error) {
        console.error("Error while updating cost center status:", error);
        showToast({
          type: ToastType.error,
          text: "Failed to update status. Try again later.",
        });
      }
    }
  };

  const onSaveAsDraft = async () => {
    // if (selectedType === "Product") {
    //   if (is_update_page && !productData._id && serviceData._id) {
    //     productData._id = serviceData._id;
    //     productData.item_code = serviceData.item_code;
    //     productData.id = serviceData.id;
    //   }

    //   console.log("onSubmit", productData);
    //   productData.type = selectedType;
    //   productData.status = MasterItemStatus.SavedDraft;
    //   if (!productData.name) {
    //     showToast({
    //       type: ToastType.error,
    //       text: "Please fill required fields !",
    //     });
    //     return;
    //   }
    //   // TODO: Check if tax applicable is there or not
    //   setLoading(true);
    //   const res = await saveAsDraftRepo(productData);
    //   if (res.success) {
    //     if (!productData.id) {
    //       setProductData(ItemModelV2Default);
    //     }
    //     console.log("res", res);
    //     setProductData(res.data);
    //     // setData((prevData) => ({
    //     //   ...prevData,
    //     //   ...res.data,
    //     // }));
    //     history.goBack();
    //     showToast({ type: ToastType.success, text: res.message });
    //     if (props.onClose) props.onClose(res.data);
    //   } else showToast({ type: ToastType.error, text: res.error });
    // } else if (selectedType === "Service") {
    //   if (is_update_page && !serviceData._id && productData._id) {
    //     serviceData._id = productData._id;
    //     serviceData.item_code = productData.item_code;
    //     serviceData.id = productData.id;
    //   }
    //   console.log("onSubmit SERVICE DATA", serviceData);
    //   serviceData.type = selectedType;
    //   serviceData.status = MasterItemStatus.SavedDraft;
    //   if (!serviceData.name) {
    //     showToast({
    //       type: ToastType.error,
    //       text: "Please fill required fields !",
    //     });
    //     return;
    //   }
    //   setLoading(true);
    //   const res = await saveAsDraftRepo(serviceData);
    //   if (res.success) {
    //     if (!serviceData.id) {
    //       setServiceData(ItemModelV2Default);
    //     }
    //     console.log("res", res);
    //     setServiceData(res.data);
    //     // setData((prevData) => ({
    //     //   ...prevData,
    //     //   ...res.data,
    //     // }));
    //     history.goBack();
    //     showToast({ type: ToastType.success, text: res.message });
    //     if (props.onClose) props.onClose(res.data);
    //   } else showToast({ type: ToastType.error, text: res.error });
    // }
    setLoading(false);
  };

  const onSubmit = async () => {
    try {
      if (!data.name || !data.budget) {
        showToast({
          type: ToastType.error,
          text: "Please fill all the required details",
        });
        return;
      }

      if (data.id && data.totalBudget < (data.spent ?? 0)) {
        showToast({
          type: ToastType.error,
          text: `Total budget can't be less than the amount already allocated to projects (${data.spent}).`,
        });
        return;
      }

      setLoading(true);

      const res = data.id
        ? await fetchPut(`${url}/${data.id}`, data)
        : await fetchPost(url, data);

      if (res.success) {
        showToast({ type: ToastType.success, text: res.message });
        if (!data.id) {
          setData(costCenterDefaultValue);
          // setShowSucess(true);
        }
        setData((prevData) => ({
          ...prevData,
          ...res.data, // Update with the response data
        }));
        if (props.onClose) props.onClose(res.data);
        navaigation.push(urls.procurement_masters_cost_center);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      console.error("Error submitting data:", error);
      showToast({ type: ToastType.error, text: "An error occurred" });
    } finally {
      setLoading(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    // TODO: check if user has permission to update
    // if maker we should be ab
    const maker_update_result = isAuthorised({
      module: MODULE.cost_center,
      action: [ACTION.UPDATE],
    });
    const checker_update_result = isAuthorised({
      module: MODULE.cost_center_approval_L1 || MODULE.cost_center_approval_L2,
      action: [ACTION.UPDATE],
    });
    if (maker_update_result && !checker_update_result) {
      // setCanEdit(true);
      setMaker(true);
      setChecker(false);
    } else if (checker_update_result && !maker_update_result) {
      // setCanEdit(false);
      setChecker(true);
      setMaker(false);
    } else if (maker_update_result && checker_update_result) {
      // setCanEdit(true);
      setMaker(true);
      setChecker(true);
    } else {
      // setCanEdit(false);
      setMaker(false);
      setChecker(false);
    }
  }, [id, data.budget]);

  useEffect(() => {
    // Once approved, changes are relative to the enforced limit, not the original budget.
    const base = data.approved_at
      ? (data.lastApprovedBudget ?? 0)
      : data.budget;
    const total = base + (data.addAdditionalBudget || 0);
    setData((prevData) => ({
      ...prevData,
      totalBudget: total,
    }));
  }, [
    data.budget,
    data.addAdditionalBudget,
    data.approved_at,
    data.lastApprovedBudget,
  ]);

  // Handle the budget input change
  // const handleBudgetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value === '' ? 0 : Number(e.target.value);
  //   // Update budget value directly in data state
  //   setData((prevData) => ({
  //     ...prevData,
  //     budget: value,
  //   }));
  // };

  return (
    <div className="mobile-shrink">
      {/* {setReject && (
        <ModalDialog
          show={reject}
          title={"Cost Center Status: Reject"}
          onClose={() => {
            setReject(false);
            setData({
              ...data,
              approvalStatus: MasterCostCenterStatus.pendingForApproval,
            });
          }}
        >
          <div className="bg-white p-6 rounded-md shadow-lg w-full md:w-96">
            <h2 className="text-md font-semibold mb-4">
              Do you want to Reject?
            </h2>
            <div className="flex flex-col gap-1">
              <label className="text-sm text-gray-500">Reason *</label>
              <textarea
                value={data.remarks}
                onChange={(e) => {
                  setData({ ...data, remarks: e.target.value });
                }}
                className="w-full px-3 py-2 border rounded-md"
                rows={4}
              ></textarea>
            </div>
            <div className="flex justify-between mt-4">
              <button
                onClick={() => {
                  setReject(false);
                  setData({
                    ...data,
                    approvalStatus: MasterCostCenterStatus.pendingForApproval,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                onClick={() => {
                  CostCenterApprovalStatus(MasterCostCenterStatus.rejected);
                  setData({
                    ...data,
                    approvalStatus: MasterCostCenterStatus.rejected,
                  });
                }}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )}
      {setApproval && (
        <ModalDialog
          show={approval}
          title={"Cost Center Status: Approve"}
          onClose={() => {
            setApproval(false);
            setData({
              ...data,
              approvalStatus: MasterCostCenterStatus.pendingForApproval,
            });
          }}
        >
          <div className="bg-white p-6 rounded-md shadow-lg w-full md:w-96">
            <h2 className="text-md font-semibold mb-4">
              Do you want to Approve?
            </h2>

            <div className="flex justify-between mt-4">
              <button
                onClick={() => {
                  setApproval(false);
                  setData({
                    ...data,
                    approvalStatus: MasterCostCenterStatus.pendingForApproval,
                  });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-700"
              >
                No
              </button>
              <button
                onClick={() => {
                  CostCenterApprovalStatus(MasterCostCenterStatus.approved);
                }}
                className="bg-green-500 text-white px-4 py-2 rounded-md hover:bg-blue-600"
              >
                Yes
              </button>
            </div>
          </div>
        </ModalDialog>
      )} */}

      <div className="flex justify-between items-end my-1">
        <h1 className="flex text-xl font-bold border-l-4 border-myPrimaryColor mx-2 md:mx-5 pl-2">
          Cost Center {id ? `Update: ${data.id}` : "Create"}
        </h1>
      </div>
      <div id="cost_center_create_form" className="mb-10">
        <div className="p-3 md:p-5 m-2 md:m-5 flex flex-col gap-5 shadow bg-white rounded-lg">
          <div className="flex flex-col items-start text-sm">
            <label className="tesxt-sm font-semibold w-full" htmlFor="name">
              Cost Center Name*
            </label>
            <input
              placeholder="Enter Name"
              className="p-1 rounded focus:outline-none w-full md:w-1/2 border"
              id="name"
              name="name"
              value={data.name || ""}
              required
              type="text"
              onChange={(e) => setData({ ...data, name: e.target.value })}
            />
          </div>

          {/* --- BU + Department selectors. If picked, Finance creates the CC already
              approved (new flow) and later budget changes go through Budget Increase
              Requests. If left blank, the CC uses the legacy L1/L2 approval flow.
              Disabled on edit screens to prevent reassignment from this UI. */}
          <div className="grid md:grid-cols-2 gap-5">
            <div className="flex flex-col items-start text-sm w-full">
              <label className="font-semibold w-full" htmlFor="business_unit_id">
                Business Unit
              </label>
              <select
                id="business_unit_id"
                className="p-1 rounded focus:outline-none w-full border bg-white disabled:bg-gray-100"
                value={data.business_unit_id || ""}
                disabled={edit}
                onChange={(e) => {
                  const bu = businessUnits.find((b) => b.id === e.target.value);
                  setData({
                    ...data,
                    business_unit_id: bu?.id,
                    business_unit_name: bu?.name,
                    business_unit_code: bu?.code,
                  });
                }}
              >
                <option value="">— None (legacy flow) —</option>
                {businessUnits.map((bu) => (
                  <option key={bu.id} value={bu.id}>
                    {bu.name} ({bu.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col items-start text-sm w-full">
              <label className="font-semibold w-full" htmlFor="department_id">
                Department {data.business_unit_id && <span>*</span>}
              </label>
              <select
                id="department_id"
                className="p-1 rounded focus:outline-none w-full border bg-white disabled:bg-gray-100"
                value={data.department_id || ""}
                disabled={edit}
                onChange={(e) => {
                  const dep = departments.find((d) => d.id === e.target.value);
                  setData({
                    ...data,
                    department_id: dep?.id,
                    department_name: dep?.name,
                    department_code: dep?.code,
                  });
                }}
              >
                <option value="">— None —</option>
                {departments.map((dep) => (
                  <option key={dep.id} value={dep.id}>
                    {dep.name} ({dep.code})
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-col items-start text-sm w-full">
            <label className="font-semibold w-full" htmlFor="desc">
              Description
            </label>
            <textarea
              placeholder="Short description"
              className="p-1 rounded focus:outline-none w-full border"
              id="description"
              name="description"
              value={data.description || ""}
              onChange={(e) =>
                setData((d) => ({ ...d, description: e.target.value }))
              }
            ></textarea>
          </div>

          <div className="grid md:grid-cols-3 gap-5 items-end">
            <div className="flex flex-col items-start text-sm w-full">
              <label className="font-semibold w-full" htmlFor="type">
                From Month*
              </label>
              <input
                type="date"
                id="from_month"
                className="border px-7 py-1 rounded-md w-full"
                value={
                  data?.fromMonth
                    ? moment(data?.fromMonth).format("YYYY-MM-DD")
                    : ""
                }
                onChange={(e) => {
                  const selectedDate = e.target.value; // Get the selected date string (YYYY-MM-DD)
                  const date = selectedDate
                    ? new Date(new Date(selectedDate).setHours(0, 0, 0, 0)) // Convert to a date object
                    : undefined; // Reset if no date is selected

                  // Update the paymentRequest state with the selected date
                  setData((pv) => ({
                    ...pv,
                    fromMonth: date!, // Save the selected date in state
                  }));
                }}
              />
            </div>
            <div className="flex flex-col items-start text-sm w-full">
              <label className="font-semibold w-full" htmlFor="type">
                To Month*
              </label>
              <input
                type="date"
                id="to_month"
                className="border px-7 py-1 rounded-md w-full"
                value={
                  data?.toMonth
                    ? moment(data?.toMonth).format("YYYY-MM-DD")
                    : ""
                }
                onChange={(e) => {
                  const selectedDate = e.target.value; // Get the selected date string (YYYY-MM-DD)
                  const date = selectedDate
                    ? new Date(new Date(selectedDate).setHours(0, 0, 0, 0)) // Convert to a date object
                    : undefined; // Reset if no date is selected

                  // Update the paymentRequest state with the selected date
                  setData((pv) => ({
                    ...pv,
                    toMonth: date!, // Save the selected date in state
                  }));
                }}
              />
            </div>
            <div className="flex flex-col items-start text-sm w-full">
              <label className="font-semibold w-full" htmlFor="type">
                Period*
              </label>
              <input
                type="number"
                id="period"
                className="font-semibold w-full border-2 rounded-md p-1"
                value={data.period}
                // onChange={(e) => setData({ ...data, period: periodInMonths})}
                readOnly
              />
            </div>
          </div>
          <div className="flex flex-col items-start text-sm w-full md:w-1/4">
            <label className="font-semibold w-full" htmlFor="name">
              Budget*
            </label>
            <input
              disabled={edit && !!data.approved_at}
              placeholder="Enter budget"
              className="p-1 rounded focus:outline-none w-full border"
              id="budget"
              name="budget"
              value={data.budget || ""} // Correctly bind the value to data.budget
              required
              type="number"
              onChange={(e) => {
                // Get the new value from the input
                const newValue =
                  e.target.value === "" ? 0 : Number(e.target.value);

                // Correctly update the budget field in the data state
                setData((prevData) => ({
                  ...prevData,
                  budget: newValue, // Update budget correctly
                }));
              }}
            />
          </div>
          <div className="flex md:flex-row gap-4">
            {id && !!data.approved_at && (
              <div className="flex flex-col md:flex-row gap-4 w-full md:w-1/2">
                {data.business_unit_id ? (
                  <div
                    id="cc_budget_change_hint"
                    className="flex flex-col items-start text-sm w-full"
                  >
                    <label className="font-semibold w-full">
                      Additional Budget / Reduction
                    </label>
                    <p className="text-xs text-gray-500 mt-1">
                      Budget changes on this cost center go through a Budget
                      Increase Request from the cost center page.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-start text-sm w-full">
                    <label className="font-semibold w-full" htmlFor="name">
                      Additional Budget / Reduction*
                    </label>
                    <input
                      placeholder="budget"
                      className="p-1 rounded focus:outline-none w-full border"
                      id="add_budget"
                      name="add_budget"
                      value={data.addAdditionalBudget || ""}
                      required
                      type="number"
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        setData({
                          ...data,
                          addAdditionalBudget: value,
                          duplicateAdditionalBudget: value, // Ensure both values are synced
                        });
                      }}
                    />
                    <span className="mt-2">
                      <label className="text-green-500 w-full">
                        Approved add Amounts:
                      </label>
                      <p className="text-sm">
                        {data.additionalFunds
                          ?.map((fund, index) => fund.fund_amount)
                          .join(", ")}
                      </p>
                    </span>
                  </div>
                )}
                <div className="flex flex-col items-start text-sm w-full">
                  <label className="font-semibold w-full" htmlFor="name">
                    Last Approved Budget
                  </label>
                  <input
                    disabled={edit}
                    placeholder="budget"
                    className="p-1 rounded focus:outline-none w-full border"
                    id="add_budget"
                    name="add_budget"
                    value={data.lastApprovedBudget || ""}
                    required
                    type="number"
                    onChange={(e) =>
                      setData({
                        ...data,
                        lastApprovedBudget: Number(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col items-start text-sm">
            <label className="font-semibold w-full" htmlFor="name">
              Total Budget*
            </label>
            <input
              placeholder="total budget"
              className="p-1 rounded focus:outline-none w-full md:w-1/4 border"
              id="tot_budget"
              name="tot_budget"
              value={data.totalBudget || ""}
              required
              type="number"
              readOnly
            />
          </div>
          <div className="flex flex-col md:flex-row gap-5 w-full">
            <div className=" w-full md:w-1/2">
              <div>
                <label className="text-sm font-bold">
                  Balance is calculated based on the last approved budget.
                </label>
              </div>
              <div className="bg-gray-100 p-3 md:p-5 rounded-md">
                <div className="flex flex-col items-start text-sm">
                  <label className="font-semibold w-full" htmlFor="name">
                    Spent
                  </label>
                  <input
                    placeholder=""
                    className="p-1 rounded focus:outline-none w-full border"
                    id="spent"
                    name="spent"
                    value={data.spent || ""}
                    required
                    type="number"
                    readOnly
                  />
                </div>
                <div className="flex flex-col items-start text-sm">
                  <label className="font-semibold w-full" htmlFor="name">
                    Balance{" "}
                  </label>
                  <input
                    placeholder=""
                    className="p-1 rounded focus:outline-none w-full border"
                    id="balance"
                    name="balance"
                    value={data.lastApprovedBudget - data.spent || ""}
                    required
                    type="number"
                    readOnly
                  />
                </div>
              </div>
            </div>
            <div className="flex flex-col items-start text-sm w-full md:w-1/2">
              <label className="font-semibold w-full" htmlFor="desc">
                Notes/Remarks
              </label>
              <textarea
                placeholder="remarks"
                className="p-1 mt-1 rounded focus:outline-none w-full border"
                id="remarks"
                name="remarks"
                rows={6}
                value={data.remarks || ""}
                onChange={(e) =>
                  setData((d) => ({ ...d, remarks: e.target.value }))
                }
              ></textarea>
            </div>
          </div>
        </div>
      </div>
      <div className="pb-10">
        <div className="mb-10">
          {maker && !id && (
            <div className="flex flex-wrap justify-end gap-5 my-5 mr-2 md:mr-5">
              <button
                id="create_btn"
                onClick={onSubmit}
                type="button"
                className="bg-myPrimaryColor text-white rounded-lg px-6 py-1.5 text-sm cursor-pointer duration-300"
              >
                Create
              </button>
              <button
                id="quit_btn"
                onClick={() => {
                  navaigation.push(urls.procurement_masters_cost_center);
                }}
                type="button"
                className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
              >
                Quit
              </button>
            </div>
          )}{" "}
        </div>
        <div className=" my-10">
          {maker && id && (
            <div className="flex flex-wrap justify-end gap-5 my-5 mr-2 md:mr-5">
              {!(data.costCenterStatus === CostCenterCloseStatus.closed) && (
                <button
                  id="update_btn"
                  onClick={onSubmit}
                  type="button"
                  className="bg-myPrimaryColor text-white rounded-lg px-6 py-1.5 text-sm cursor-pointer duration-300"
                >
                  Update
                </button>
              )}

              <button
                id="quit_btn"
                onClick={() => {
                  navaigation.push(urls.procurement_masters_cost_center);
                }}
                type="button"
                className="rounded-md border border-green-600 text-sm font-semibold bg-white text-black px-3 py-1"
              >
                Quit
              </button>
            </div>
          )}{" "}
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
};
export default CostCenterCreateScreen;
